import styled, { css } from 'styled-components';
import createTheme from 'styled-variants';
import { buildResponsiveValueCss, getShadowStyle, pxToRems, } from '@awaze/design-system.utils.react';
const variants = ({ theme, isGhostIconButton }) => {
    return {
        elevated: {
            background: theme.ColorWhiteOpacity100,
            boxShadow: getShadowStyle({ theme, depth: 2 }),
            color: theme.ColorThemeLink,
            '&:hover, &:focus': {
                color: theme.ColorThemeLinkActive,
            },
        },
        ghost: {
            background: 'transparent',
            ...(!isGhostIconButton && { paddingLeft: 0, paddingRight: 0 }),
        },
        outline: {
            background: 'transparent',
            border: '2px solid',
            padding: ({ size }) => size === 'small' ? '8px' : '10px 14px',
            '&:hover, &:focus': {
                background: 'transparent',
            },
        },
        text: {
            background: 'transparent',
            textDecoration: 'underline',
            padding: '0px',
            '&:hover, &:focus': {
                background: 'transparent',
                boxShadow: '0 0 0 4px ${theme.ColorBlackOpacity10} inset',
            },
        },
        darkTransparency: {
            background: theme.ColorBlackOpacity50,
            color: theme.ColorGrey25,
            '&:hover': {
                background: theme.ColorWhiteOpacity50,
                color: theme.ColorBlackOpacity50,
            },
        },
    };
};
const buttonJustification = () => ({
    center: {
        justifyContent: 'center',
    },
    'space-between': {
        justifyContent: 'space-between',
    },
});
const colourSchemes = ({ theme, variant }) => {
    const isGhost = variant === 'ghost';
    const isOutline = variant === 'outline';
    const isElevated = variant === 'elevated';
    const isText = variant === 'text';
    return !isElevated
        ? {
            'brand-primary': {
                background: theme.ColorButtonSolidPrimaryBackground,
                color: isOutline || isText
                    ? theme.ColorButtonOutlinePrimaryText
                    : isGhost
                        ? theme.ColorButtonGhostPrimaryText
                        : theme.ColorButtonSolidPrimaryText,
                borderColor: isOutline ? theme.ColorButtonGhostPrimaryText : null,
                '&:hover, &:focus': {
                    background: isGhost || isText
                        ? 'transparent'
                        : theme.ColorButtonSolidPrimaryBackgroundHover,
                    color: isOutline
                        ? theme.ColorButtonOutlinePrimaryTextHover
                        : isGhost
                            ? theme.ColorButtonGhostPrimaryTextHover
                            : null,
                    borderColor: isOutline
                        ? theme.ColorButtonGhostPrimaryTextHover
                        : null,
                },
            },
            'brand-secondary': {
                background: theme.ColorButtonSolidSecondaryBackground,
                color: isOutline || isText
                    ? theme.ColorButtonOutlineSecondaryText
                    : isGhost
                        ? theme.ColorButtonGhostSecondaryText
                        : theme.ColorButtonSolidSecondaryText,
                borderColor: isOutline ? theme.ColorButtonGhostSecondaryText : null,
                '&:hover, &:focus': {
                    background: isGhost || isText
                        ? 'transparent'
                        : theme.ColorButtonSolidSecondaryBackgroundHover,
                    color: isOutline
                        ? theme.ColorButtonOutlineSecondaryTextHover
                        : isGhost
                            ? theme.ColorButtonGhostSecondaryTextHover
                            : null,
                    borderColor: isOutline
                        ? theme.ColorButtonGhostSecondaryTextHover
                        : null,
                },
            },
            'brand-tertiary': {
                background: theme.ColorButtonSolidTertiaryBackground,
                color: isOutline || isText
                    ? theme.ColorButtonOutlineTertiaryText
                    : isGhost
                        ? theme.ColorButtonGhostTertiaryText
                        : theme.ColorButtonSolidTertiaryText,
                borderColor: isOutline ? theme.ColorButtonGhostTertiaryText : null,
                '&:hover, &:focus': {
                    background: isGhost || isText
                        ? 'transparent'
                        : theme.ColorButtonSolidTertiaryBackgroundHover,
                    color: isOutline
                        ? theme.ColorButtonOutlineTertiaryTextHover
                        : isGhost
                            ? theme.ColorButtonGhostTertiaryTextHover
                            : null,
                    borderColor: isOutline
                        ? theme.ColorButtonGhostTertiaryTextHover
                        : null,
                },
            },
            reversed: {
                background: theme.ColorButtonSolidReverseBackground,
                color: isOutline || isText
                    ? theme.ColorButtonOutlineReverseText
                    : isGhost
                        ? theme.ColorButtonGhostReverseText
                        : theme.ColorButtonSolidReverseText,
                borderColor: isOutline ? theme.ColorButtonGhostReverseText : null,
                '&:hover, &:focus': {
                    background: isGhost || isText
                        ? 'transparent'
                        : theme.ColorButtonSolidReverseBackgroundHover,
                    color: isOutline
                        ? theme.ColorButtonOutlineReverseTextHover
                        : isGhost
                            ? theme.ColorButtonGhostReverseTextHover
                            : null,
                    borderColor: isOutline
                        ? theme.ColorButtonGhostReverseTextHover
                        : null,
                },
            },
        }
        : {};
};
export const commonStyling = ({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: ({ gap }) => gap ?? '12px',
    flexDirection: ({ iconPosition }) => iconPosition === 'left' ? 'row-reverse' : null,
    padding: ({ size, padding }) => padding || (size === 'small' ? '10px' : '12px 16px'),
    '-webkit-appearance': 'none',
    textDecoration: 'none',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color box-shadow 100ms ease',
    boxShadow: `0 0 0 4px transparent`,
    lineHeight: pxToRems(theme.FontTextText300MidLineHeight),
    fontSize: ({ size }) => size === 'small'
        ? pxToRems(theme.FontTextText200FontSize)
        : pxToRems(theme.FontTextText300FontSize),
    rounded: {
        borderRadius: '50%',
    },
    isDisabled: {
        opacity: 0.3,
        cursor: 'default',
        pointerEvents: 'none',
    },
    isGhostIconButton: {
        border: 'none',
    },
    '&:focus': {
        outline: 'none',
        boxShadow: `0 0 0 4px ${theme.ColorBlackOpacity10}`,
    },
});
export const StyledIcon = styled.span `
  ${({ size }) => size === 'small'
    ? 'width: 20px; height: 20px;'
    : 'width: 24px; height: 24px;'}
  display: flex;
  align-items: center;
  justify-content: center;

  ${(p) => p.iconColor === 'current' &&
    css `
      svg path {
        fill: currentColor;
      }
    `}
`;
export const ButtonTheme = createTheme('Button', commonStyling)
    .addVariant('colourScheme', colourSchemes)
    .addVariant('variant', variants)
    .addVariant('justify', buttonJustification);
const ThemedButton = styled.button(ButtonTheme);
export const ButtonStyled = styled(ThemedButton) `
  ${({ isFullWidth }) => buildResponsiveValueCss({
    property: 'width',
    values: isFullWidth,
    evaluate: (value) => (value ? '100%' : 'auto'),
})}
`;
export const ButtonStyledIcon = styled(ThemedButton) `
  padding: ${({ size, variant }) => size === 'small' || variant === 'outline' ? '10px' : '12px'};
`;
//# sourceMappingURL=button.styles.js.map